'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StudioVideoStoreProvider } from '@/features/ai-video-creator/stores/studioVideoStore';
import { BlockStack, Button, Form, FormLayout, InlineStack, Layout } from '@shopify/polaris';
import ScrollableThumbnailSelect from '@/components/images/ScrollableThumbnailSelect';
import { ImageToVideo } from '@/components/videos/ImageToVideo';
import {
  MediaModalSectionName,
  SECTIONS_WITHOUT_TOOLS,
  SECTIONS_WITHOUT_TOOLS_AND_STOCK,
} from '@/features/media-modal/components/common/commonTypes';
import { useToast } from '@/hooks/useToast';
import AddMediaModal from '@/features/media-modal/components/AddMediaModal';
import {
  AssetCreationToolName,
  GenerationInstrument,
  ICardMediaAsset,
  MediaAsset,
  MediaAssetType,
  MediaAssetVideoProvider,
} from '@/types/media';
import { ImageWithProductId as Image } from '@/types/media';

import { useSearchParams } from 'next/navigation';
import { getPreselectedMediaAssets } from '@/features/ai-video-creator/hooks/useProductMedia';
import { useCurrentApp } from '@/stores/userDataStore';
import { useImageToVideo } from '@/hooks/video/useImageToVideo';
import { PricesService } from '@/features/billing';
import { useInsufficientBalance } from '@/features/billing/components/InsufficientBalanceContext';
import useSaveBar from '@/hooks/useSaveBar';
import useFormDataState, { FormName } from '@/hooks/useFormDataState';
import usePageNavigation from '@/hooks/usePageNavigation';
import GeneratedMediaCard from '@/components/media-asset/ai-tools/GeneratedMediaCard';
import { Page } from '@/components/Page';
import { MediaAssetPollingProvider } from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import { BannerState, SessionBanner } from '@/types/banners';
import { useQueryClient } from '@tanstack/react-query';
import DismissibleBanner from '@/components/DismissibleBanner';
import BasicBanner from '@/components/BasicBanner';
import { PriceModifierType } from '@/features/billing/services/CreditsBillingService';
import { CreditsPriceItem } from '@/features/billing/services/CreditsBillingService';
import { Modifier } from '@/features/billing/services/CreditsBillingService';

export enum VideoMediaAssetDuration {
  Default = 'default',
  Long = 'long',
}

export enum VideoMediaAssetMode {
  pro = 'pro',
  std = 'std',
}

export const AIVideoAssetCreatorComponent = memo(() => {
  const { checkBalance } = useInsufficientBalance();
  const { isError, isGenerating, generatedMediaAsset, handleGenerate } = useImageToVideo();
  const { notifyError, notify } = useToast();
  const { appKey, isLoggedIn } = useCurrentApp();
  const searchParams = useSearchParams();
  const { formData, handleSetFormData, handleClearFormData } = useFormDataState(FormName.AIVideoAssetCreator);

  const [error, setError] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [videoPrompt, setVideoPrompt] = useState<string>(formData.videoPrompt || '');
  const [selectedImage, setSelectedImage] = useState<Image | null>(formData.selectedImage || null);
  const [images, setImages] = useState<Image[]>(formData.images || []);
  const [videoDuration, setVideoDuration] = useState<VideoMediaAssetDuration>(
    formData.videoDuration || VideoMediaAssetDuration.Default
  );
  const [videoMode, setVideoMode] = useState<VideoMediaAssetMode>(formData.videoMode || VideoMediaAssetMode.std);

  const CREDITS = useMemo(() => {
    // FIXME: this is temporary - we should fetch credits from server instead
    const modifiers: Modifier[] = [];
    if (videoDuration === VideoMediaAssetDuration.Long) {
      modifiers.push({
        type: PriceModifierType.MULTIPLY,
        value: 2,
      });
    }
    if (videoMode === VideoMediaAssetMode.pro) {
      modifiers.push({
        type: PriceModifierType.MULTIPLY,
        value: 2,
      });
    }

    return PricesService.getPriceText(
      AssetCreationToolName.animateImage,
      [GenerationInstrument.imageToVideo],
      1,
      modifiers
    );
  }, [videoDuration, videoMode]);
  const CREDITS_TEXT = CREDITS ? ` (${CREDITS})` : '';

  const ALLOWED_SECTIONS = [
    ...SECTIONS_WITHOUT_TOOLS.filter(
      (section) => ![MediaModalSectionName.bunuVideos, MediaModalSectionName.playApp].includes(section)
    ),
  ];
  const hasInitPreselected = useRef(false);

  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setImages(formData.images || []);
    setSelectedImage(formData.selectedImage);
    setVideoPrompt(formData.videoPrompt || '');
  }, [formData]);

  const handleSaveDraft = useCallback(() => {
    handleSetFormData({
      images,
      selectedImage,
      videoPrompt,
      videoDuration,
      videoMode,
    });
    notify('Draft saved');
  }, [images, selectedImage, videoPrompt, videoDuration, videoMode, handleSetFormData, notify]);

  const handleDiscard = useCallback(() => {
    setImages(formData.images || []);
    setSelectedImage(formData.selectedImage);
    setVideoPrompt(formData.videoPrompt || '');
  }, [formData]);

  const hasFormChanged = useMemo(() => {
    return (
      videoPrompt !== formData.videoPrompt || images !== formData.images || selectedImage !== formData.selectedImage
    );
  }, [formData, videoPrompt, images, selectedImage]);

  const isDirty = hasFormChanged && (!!videoPrompt || !!images.length || !!selectedImage);
  const { forceHideSaveBar } = useSaveBar({
    onSave: handleSaveDraft,
    onDiscard: handleDiscard,
    isDirty,
  });

  useEffect(() => {
    if (hasInitPreselected.current || !isLoggedIn || !appKey || !searchParams || formData.images?.length > 0) {
      return;
    }

    hasInitPreselected.current = true;

    getPreselectedMediaAssets({ appKey, searchParams }).then((preselectedMediaAssets) => {
      setImages((existingImages) => [
        ...existingImages,
        ...preselectedMediaAssets.map((item) => ({
          id: item.id,
          src: item.src,
          productId: item.provider === 'product' ? item.id : undefined,
          alt: item.alt,
        })),
      ]);
    });
  }, [appKey, searchParams, formData.images, isLoggedIn]);

  const handleAddMedia = useCallback(
    (media: MediaAsset[]) => {
      const hasVideo = media.some((item) => item.type === MediaAssetType.video);
      if (hasVideo) {
        setError('Videos are not supported for background image');
        return;
      }

      const newImages = media.map((item) => ({
        id: item.id,
        src: item.src,
        alt: item.alt,
        productId: item.provider === 'product' ? item.id : undefined,
      }));
      setImages((prev) => [...prev, ...newImages]);
    },
    [setImages]
  );

  const handleMoreClick = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleGenerateAIVideo = useCallback(async () => {
    pageRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

    if (!checkBalance(AssetCreationToolName.animateImage, [GenerationInstrument.imageToVideo])) return;
    forceHideSaveBar();
    await handleGenerate({
      imageSrc: selectedImage?.src,
      videoPrompt,
      productId: selectedImage?.productId,
      duration: videoDuration,
      mode: videoMode,
    });
    handleClearFormData(true);
  }, [
    checkBalance,
    handleGenerate,
    selectedImage,
    videoPrompt,
    videoDuration,
    videoMode,
    handleClearFormData,
    forceHideSaveBar,
  ]);

  const { navigateBack, previousPageTitle } = usePageNavigation();

  return (
    <Page backAction={{ content: previousPageTitle, onAction: navigateBack }} title="Animate image" ref={pageRef}>
      <BlockStack gap="400">
        <BasicBanner tone="critical" title="Invalid media" hidden={!error} onDismiss={() => setError('')}>
          {error}
        </BasicBanner>
        <DismissibleBanner
          tone="critical"
          title="Generation failed"
          sessionBanner={SessionBanner.ImageToVideoGenerationError}
          showBanner={isError}
        >
          Error generating video. Please try again or contact support.
        </DismissibleBanner>

        <Layout>
          <Layout.Section>
            <Form noValidate onSubmit={() => {}}>
              <FormLayout>
                <ScrollableThumbnailSelect
                  title="Select an image"
                  description="Choose or upload an image to animate"
                  items={images}
                  selected={selectedImage}
                  onSelect={setSelectedImage}
                  onMoreClick={handleMoreClick}
                />

                <ImageToVideo
                  videoPrompt={videoPrompt}
                  setVideoPrompt={setVideoPrompt}
                  isError={isError}
                  isGenerating={isGenerating}
                  setVideoDuration={setVideoDuration}
                  videoDuration={videoDuration}
                  setVideoMode={setVideoMode}
                  videoMode={videoMode}
                />

                <AddMediaModal
                  allowedSections={ALLOWED_SECTIONS}
                  mediaAssetType={MediaAssetType.image}
                  addMedia={handleAddMedia}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  initialSection={MediaModalSectionName.upload}
                  isOnlyImages
                />
              </FormLayout>
            </Form>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <GeneratedMediaCard
              title="Generated"
              subtitle="Enter a prompt to generate a video"
              media={generatedMediaAsset as ICardMediaAsset}
              mediaType="Video"
              showProgressBannerWarning={true}
            />
          </Layout.Section>
          <Layout.Section>
            <InlineStack align="end">
              <Button onClick={handleGenerateAIVideo} variant="primary" disabled={isGenerating || !selectedImage}>
                {`Generate${CREDITS_TEXT}`}
              </Button>
            </InlineStack>
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
});

AIVideoAssetCreatorComponent.displayName = 'AIVideoAssetCreatorComponent';

export const AIVideoAssetCreator = (props) => {
  const queryClient = useQueryClient();
  const initStateFromServerPage = useRef(false);

  if (!initStateFromServerPage.current) {
    initStateFromServerPage.current = true;
    if (props.isAnimateToolContentModerationBannerHidden) {
      queryClient.setQueryData(['bannerState', SessionBanner.AnimateToolContentModeration], BannerState.CLOSED);
    }
    if (props.isFinalVideoAvailableBannerHidden) {
      queryClient.setQueryData(['bannerState', SessionBanner.FinalVideoAvailable], BannerState.CLOSED);
    }
    if (props.isImageToVideoGenerationErrorBannerHidden) {
      queryClient.setQueryData(['bannerState', SessionBanner.ImageToVideoGenerationError], BannerState.CLOSED);
    }
  }

  return (
    <MediaAssetPollingProvider>
      <StudioVideoStoreProvider>
        <AIVideoAssetCreatorComponent {...props} />
      </StudioVideoStoreProvider>
    </MediaAssetPollingProvider>
  );
};
